/*
 * HDS predictive-search prototype patches. Primavera's predictive-search was
 * built for a fullscreen modal; in the HDS dropdown panel three methods need
 * tweaks (open, renderSearchResults, switchOption). Patches are scoped to
 * instances whose results container is `.hds-header__search-results`, so any
 * other predictive-search on the page keeps Primavera's behavior.
 */
(function () {
  'use strict';

  function isHdsInstance(el) {
    return !!el.predictiveSearchResults?.classList?.contains('hds-header__search-results');
  }

  function patch(PredictiveSearch) {
    if (!PredictiveSearch || PredictiveSearch.prototype.__hdsPatched) return;
    const proto = PredictiveSearch.prototype;
    const originalOpen = proto.open;
    const originalRender = proto.renderSearchResults;
    const originalSwitch = proto.switchOption;
    const originalCloseResults = proto.closeResults;

    proto.open = function () {
      if (!isHdsInstance(this)) return originalOpen.call(this);
      // Skip the inline maxHeight from getResultsMaxHeight() — that selector
      // (`.section-header`) doesn't match in HDS. Height is capped via CSS in
      // hds-search.css (.hds-header__search-results { max-block-size: ... }).
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    };

    proto.renderSearchResults = function (resultsMarkup) {
      if (!isHdsInstance(this)) return originalRender.call(this, resultsMarkup);
      const prevScroll = this.predictiveSearchResults.scrollTop;
      originalRender.call(this, resultsMarkup);
      // rAF runs after the new DOM is laid out, so scrollTop is now meaningful.
      requestAnimationFrame(() => {
        this.predictiveSearchResults.scrollTop = prevScroll;
      });
    };

    proto.closeResults = function (clearSearchTerm = false) {
      originalCloseResults.call(this, clearSearchTerm);
      if (!isHdsInstance(this)) return;
      // Base closeResults drops [open] but leaves the listbox in the DOM; remove
      // it so the panel reopens clean. Results stay cached in JS for re-typing.
      this.predictiveSearchResults
        .querySelectorAll('[data-predictive-results], [data-predictive-search-live-region-count-value]')
        .forEach((el) => el.remove());
    };

    proto.switchOption = function (direction) {
      const result = originalSwitch.call(this, direction);
      if (!isHdsInstance(this)) return result;
      // `block: 'nearest'` only scrolls the nearest scrollable ancestor that
      // needs to scroll — which is .hds-header__search-results. The page
      // doesn't scroll because the panel itself sits inside the sticky header.
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) {
        selected.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      return result;
    };

    proto.__hdsPatched = true;
  }

  const existing = customElements.get('predictive-search');
  if (existing) {
    patch(existing);
  } else {
    customElements.whenDefined('predictive-search').then(() => {
      patch(customElements.get('predictive-search'));
    });
  }

  // Clear empties the input without closing (the X closes); keep focus to retype.
  document.querySelectorAll('.hds-header__search-clear').forEach((btn) => {
    btn.addEventListener('click', () => {
      const input = btn.form?.querySelector('.hds-header__search-input');
      requestAnimationFrame(() => input?.focus({ preventScroll: true }));
    });
  });

  // Non-modal HDS overlays: opening one (or the megamenu / cart) closes the others.
  const HDS_OVERLAYS = ['search', 'localization'];
  const forceClose = (name) => document.dispatchEvent(new CustomEvent('dialog:force-close:' + name));
  const closeOthers = (except) => HDS_OVERLAYS.forEach((n) => n !== except && forceClose(n));
  HDS_OVERLAYS.forEach((name) => {
    document.addEventListener('dialog:open:' + name, () => closeOthers(name));
  });
  document.addEventListener('dialog:open:m-cart-drawer', () => closeOthers(null));
  const headerMenu = document.querySelector('header-menu');
  if (headerMenu) {
    new MutationObserver(() => {
      if (headerMenu.hasAttribute('open')) closeOthers(null);
    }).observe(headerMenu, { attributes: true, attributeFilter: ['open'] });
  }
})();
